import { useInfiniteQuery, useQueryClient, type InfiniteData } from '@tanstack/react-query'
import { CalendarDays, Loader2 } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { toast } from 'sonner'
import { useConfirm } from '@/components/ui/confirm'
import { EmptyState, Panel, Skeleton } from '@/components/ui/display'
import { MeetingRow, type MeetingRowHandlers } from './MeetingRow'
import { MeetingsToolbar } from './MeetingsToolbar'
import {
  deleteMeeting,
  renameMeeting,
  addBlankNote,
  addNote,
  addPerson,
  addPromptNote,
  cursorForDate,
  fromYmd,
  listMeetings,
  removeNote,
  setNoteScope,
  setOrganizationAccess,
  setPersonAccess,
  setRecording,
  setSpace,
  shareWithAllParticipants,
  toYmd,
  toggleTemplate,
  updateNote,
} from '@/data/meetings'
import type { Meeting, MeetingDomain, MeetingFilters, MeetingPage } from '@/data/types'

const EMPTY_FILTERS: MeetingFilters = {
  query: '',
  people: { participant: [], access: [], shared: [] },
  spaces: [],
}

/** Long enough to skip the intermediate words, short enough to feel live. */
const SEARCH_DEBOUNCE_MS = 200

function dayLabel(ymd: string): string {
  const date = fromYmd(ymd)
  if (!date) return ymd
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const days = Math.round((date.getTime() - today.getTime()) / 86_400_000)
  if (days === 0) return 'Today'
  if (days === 1) return 'Tomorrow'
  if (days === -1) return 'Yesterday'
  return date.toLocaleDateString(undefined, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    ...(date.getFullYear() === today.getFullYear() ? {} : { year: 'numeric' }),
  })
}

type Entry =
  | { kind: 'day'; key: string; items: Meeting[] }
  | { kind: 'empty-day'; key: string }

function hasFilters(filters: MeetingFilters): boolean {
  const { participant, access, shared } = filters.people
  return participant.length + access.length + shared.length + filters.spaces.length > 0
}

/**
 * Meetings overview.
 *
 * Two independent infinite lists — Upcoming scrolling forward, Past scrolling
 * back — rather than one timeline the reader has to find their place in. The
 * domain is chosen first, and every other control (date, people, spaces)
 * operates inside it.
 */
export function MeetingsPage() {
  const queryClient = useQueryClient()
  const confirm = useConfirm()

  const [domain, setDomain] = useState<MeetingDomain>('past')
  const [filters, setFilters] = useState<MeetingFilters>(EMPTY_FILTERS)
  /** Held separately from `filters` so typing does not refetch on every key. */
  const [search, setSearch] = useState('')
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const [busyId, setBusyId] = useState<string | null>(null)

  /** The day the picker parked on, and how far the list must load to reach it. */
  const [jumpDate, setJumpDate] = useState<string | null>(null)
  const [jumpIndex, setJumpIndex] = useState<number | null>(null)

  const scrollRef = useRef<HTMLDivElement>(null)
  const sentinelRef = useRef<HTMLDivElement>(null)

  const query = useInfiniteQuery({
    queryKey: ['meetings', domain, filters],
    queryFn: ({ pageParam }) => listMeetings(domain, pageParam, filters),
    initialPageParam: 0,
    getNextPageParam: (last: MeetingPage) => last.nextCursor,
  })

  const meetings = useMemo(
    () => query.data?.pages.flatMap((page) => page.items) ?? [],
    [query.data]
  )

  const { fetchNextPage, hasNextPage, isFetchingNextPage } = query

  /* ----- Paging ----- */

  /**
   * The observer only reports whether the end of the list is in view; the
   * decision to fetch is a separate effect.
   *
   * Deciding inside the observer callback stalls after two pages: react-query
   * commits the new data and clears `isFetchingNextPage` in the same render,
   * so the effect's dependencies look unchanged, the observer is never
   * re-created, and a sentinel that is STILL on screen never fires again.
   * Re-evaluating on `meetings.length` is what makes it cascade.
   */
  const [endInView, setEndInView] = useState(false)

  useEffect(() => {
    const node = sentinelRef.current
    if (!node) return
    const observer = new IntersectionObserver(
      (entries) => setEndInView(entries[0]?.isIntersecting ?? false),
      // Fetch before the sentinel is actually reached, so the list keeps its
      // scroll momentum instead of stalling at the bottom.
      { root: scrollRef.current, rootMargin: '600px' }
    )
    observer.observe(node)
    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    if (endInView && hasNextPage && !isFetchingNextPage) void fetchNextPage()
  }, [endInView, hasNextPage, isFetchingNextPage, fetchNextPage, meetings.length])

  /**
   * A date jump is "keep loading until that day is on screen".
   *
   * Written as an effect rather than an await loop because each page arrives
   * as a render, and a loop closing over `meetings` would read a stale count
   * on every iteration after the first.
   */
  useEffect(() => {
    if (jumpIndex === null) return

    if (meetings.length > jumpIndex || !hasNextPage) {
      setJumpIndex(null)
      // The day heading may only exist once this render commits.
      requestAnimationFrame(() => {
        const target = scrollRef.current?.querySelector(`[data-day="${jumpDate}"]`)
        target?.scrollIntoView({ block: 'start', behavior: 'smooth' })
      })
      return
    }

    if (!isFetchingNextPage) void fetchNextPage()
  }, [jumpDate, jumpIndex, meetings.length, hasNextPage, isFetchingNextPage, fetchNextPage])

  const jumpTo = useCallback(
    async (ymd: string) => {
      setJumpDate(ymd)
      setJumpIndex(await cursorForDate(domain, ymd, filters))
    },
    [domain, filters]
  )

  const clearJump = useCallback(() => {
    setJumpDate(null)
    setJumpIndex(null)
    scrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' })
  }, [])

  /** Switching domain or filters starts a new list; nothing carries over. */
  const resetView = useCallback(() => {
    setJumpDate(null)
    setJumpIndex(null)
    setExpandedId(null)
    scrollRef.current?.scrollTo({ top: 0 })
  }, [])

  /**
   * Clicking away closes the open meeting.
   *
   * Anywhere means anywhere — the sidebar, the toolbar, the page margin — with
   * exactly one exception. Base UI portals its popovers and menus to the
   * document body, OUTSIDE `#root`, so the share sheet, the project picker and
   * the add-note composer are not descendants of the row they belong to.
   * Testing against `#root` rather than the row alone is what stops using one
   * of them from collapsing the row out from under you.
   */
  useEffect(() => {
    if (!expandedId) return
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Element | null
      if (!target?.closest?.('#root')) return
      if (target.closest(`[data-meeting="${expandedId}"]`)) return
      setExpandedId(null)
    }
    document.addEventListener('pointerdown', onPointerDown)
    return () => document.removeEventListener('pointerdown', onPointerDown)
  }, [expandedId])

  useEffect(() => {
    if (search === filters.query) return
    const timer = setTimeout(() => {
      setFilters((current) => ({ ...current, query: search }))
      resetView()
    }, SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [search, filters.query, resetView])

  /* ----- Mutations ----- */

  /**
   * Mutations patch the cache in place rather than invalidating.
   *
   * Invalidating an infinite query refetches every loaded page, so toggling a
   * switch forty rows down would re-request everything above it and flash the
   * whole list. The store already returns the updated meeting; writing it into
   * the cache is the entire update.
   */
  const patch = useCallback(
    (updated: Meeting) => {
      queryClient.setQueriesData<InfiniteData<MeetingPage>>(
        { queryKey: ['meetings'] },
        (old) =>
          old && {
            ...old,
            pages: old.pages.map((page) => ({
              ...page,
              items: page.items.map((item) => (item.id === updated.id ? updated : item)),
            })),
          }
      )
    },
    [queryClient]
  )

  const run = useCallback(
    (meetingId: string, action: Promise<Meeting>) => {
      setBusyId(meetingId)
      void action
        .then(patch)
        .finally(() => setBusyId((current) => (current === meetingId ? null : current)))
    },
    [patch]
  )

  const handlers: MeetingRowHandlers = useMemo(
    () => ({
      onSpaceChange: (id, spaceId) => run(id, setSpace(id, spaceId)),
      onRename: (id, title) => run(id, renameMeeting(id, title)),
      onDelete: (id) => {
        void confirm({
          title: 'Delete this meeting?',
          // Named, because "this meeting" in a list of nine is not enough to
          // act on — and what goes with it is the part people do not expect.
          body: 'Its recording, transcript and notes are deleted with it. This cannot be undone.',
          confirmLabel: 'Delete',
          tone: 'danger',
        }).then((confirmed) => {
          if (!confirmed) return
          deleteMeeting(id)
            .then(() => {
              toast.success('Meeting deleted.')
              void queryClient.invalidateQueries({ queryKey: ['meetings'] })
            })
            .catch((error: Error) => toast.error(error.message))
        })
      },
      onRecordingChange: (id, recording) => run(id, setRecording(id, recording)),
      onOrganizationAccessChange: (id, scope) => run(id, setOrganizationAccess(id, scope)),
      onPersonAccessChange: (id, email, grant, language) =>
        run(id, setPersonAccess(id, email, grant, language)),
      onAddPerson: (id, email, scope, language) => run(id, addPerson(id, email, scope, language)),
      onShareAll: (id, scope, language) => run(id, shareWithAllParticipants(id, scope, language)),
      onAddNote: (id, templateId) => run(id, addNote(id, templateId)),
      onAddBlankNote: (id, title) => run(id, addBlankNote(id, title)),
      onAddPromptNote: (id, prompt) => run(id, addPromptNote(id, prompt)),
      onToggleTemplate: (id, templateId) => run(id, toggleTemplate(id, templateId)),
      onNoteScopeChange: (id, scope) => run(id, setNoteScope(id, scope)),
      onEditNote: (id, noteId, body) => run(id, updateNote(id, noteId, { body })),
      onToggleNoteShared: (id, noteId, shared) =>
        run(id, updateNote(id, noteId, { sharedExternally: shared })),
      onRemoveNote: (id, noteId) => run(id, removeNote(id, noteId)),
    }),
    [run]
  )

  /* ----- Grouping ----- */

  const entries = useMemo<Entry[]>(() => {
    const days = new Map<string, Meeting[]>()
    for (const meeting of meetings) {
      const key = toYmd(meeting.startsAt)
      const bucket = days.get(key)
      if (bucket) bucket.push(meeting)
      else days.set(key, [meeting])
    }

    const list: Entry[] = Array.from(days, ([key, items]) => ({ kind: 'day', key, items }))
    if (!jumpDate || days.has(jumpDate)) return list

    // The picked day exists but holds nothing. Saying so where it would have
    // been beats leaving the list parked somewhere with no explanation.
    const at = list.findIndex((entry) =>
      domain === 'upcoming' ? entry.key > jumpDate : entry.key < jumpDate
    )
    list.splice(at < 0 ? list.length : at, 0, { kind: 'empty-day', key: jumpDate })
    return list
  }, [meetings, jumpDate, domain])

  return (
    <div className="flex h-full min-h-0 flex-col">
      {/* Header and list share one container width, so the toolbar ends where
          the rows end rather than running off to the left of them. */}
      <div className="shrink-0 pt-6 pb-3">
        {/* Same container and padding as the list below — applied at the same
            level, or the two end up 24px out of step. */}
        <div className="mx-auto w-full max-w-6xl px-6">
          <h1 className="mb-3 text-lg font-semibold text-[var(--color-fg)]">Meetings</h1>
          <MeetingsToolbar
            domain={domain}
            onDomainChange={(next) => {
              setDomain(next)
              resetView()
            }}
            search={search}
            onSearchChange={setSearch}
            filters={filters}
            onFiltersChange={(next) => {
              setFilters(next)
              resetView()
            }}
            jumpDate={jumpDate}
            onJump={(ymd) => void jumpTo(ymd)}
            onClearJump={clearJump}
          />
        </div>
      </div>

      {/* `both-edges` rather than plain `stable`: reserving the gutter on one
          side only moves the centre of the scroll box, which puts the centred
          list half a scrollbar to the left of the toolbar above it. */}
      <div
        ref={scrollRef}
        className="min-h-0 flex-1 overflow-y-auto [scrollbar-gutter:stable_both-edges]"
      >
        <div className="mx-auto w-full max-w-6xl px-6 pb-6">
          {query.isPending ? (
            <div className="space-y-1 pt-2">
              {Array.from({ length: 8 }).map((_, index) => (
                <Skeleton key={index} className="h-11 w-full" />
              ))}
            </div>
          ) : entries.length === 0 ? (
            <EmptyState
              icon={CalendarDays}
              title={
                filters.query
                  ? `No ${domain} meetings match “${filters.query}”`
                  : hasFilters(filters)
                    ? 'No meetings match these filters'
                    : domain === 'upcoming'
                      ? 'Nothing scheduled'
                      : 'No past meetings'
              }
            />
          ) : (
            entries.map((entry) =>
              entry.kind === 'empty-day' ? (
                <div
                  key={entry.key}
                  data-day={entry.key}
                  className="flex items-baseline gap-2 py-3 text-2xs text-[var(--color-fg-subtle)]"
                >
                  <span className="font-medium">{dayLabel(entry.key)}</span>
                  <span>no meetings</span>
                </div>
              ) : (
                <section key={entry.key} data-day={entry.key} className="pt-3">
                  <h2
                    className={
                      'sticky top-0 z-10 bg-[var(--color-canvas)] pb-1.5 text-2xs font-medium ' +
                      'text-[var(--color-fg-subtle)]'
                    }
                    aria-current={entry.key === jumpDate ? 'date' : undefined}
                  >
                    {dayLabel(entry.key)}
                  </h2>
                  <Panel>
                    {entry.items.map((meeting) => (
                      <MeetingRow
                        key={meeting.id}
                        meeting={meeting}
                        expanded={expandedId === meeting.id}
                        busy={busyId === meeting.id}
                        handlers={handlers}
                        onToggle={() =>
                          setExpandedId((current) => (current === meeting.id ? null : meeting.id))
                        }
                      />
                    ))}
                  </Panel>
                </section>
              )
            )
          )}

          <div ref={sentinelRef} className="flex justify-center py-4">
            {isFetchingNextPage ? (
              <Loader2 className="size-3.5 animate-spin text-[var(--color-fg-subtle)]" />
            ) : null}
          </div>
        </div>
      </div>
    </div>
  )
}
